import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Phone, Shield, Lock, Calendar } from 'lucide-react';
import Icon from '../AppIcon';
import Button from './Button';
import { useAuth } from '../../contexts/AuthContext';
import { getDynamicPhoneNumber } from '../../services/constants';

const Header = ({ className = '' }) => {
  const { user, userProfile, loading, signOut } = useAuth();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [fallbackPhone, setFallbackPhone] = useState('+19162563468');

  // Set up dynamic fallback phone number
  useEffect(() => {
    const updateFallbackPhone = async () => {
      try {
        const dynamicPhone = await getDynamicPhoneNumber();
        setFallbackPhone(dynamicPhone);
      } catch (error) {
        setFallbackPhone('+19162563468');
      }
    };

    updateFallbackPhone();

    const handleCurrentAgentChange = () => {
      updateFallbackPhone();
    };

    window.addEventListener('currentAgentChanged', handleCurrentAgentChange);

    return () => {
      window.removeEventListener('currentAgentChanged', handleCurrentAgentChange);
    };
  }, []);

  const formatPhoneDisplay = (phone) => {
    return phone?.replace('+1', '')?.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleSignOut = async () => {
    const result = await signOut();
    if (result?.success) {
      window.location.href = '/admin-login';
    }
  };

  // NEW: Direct navigation handler for logo click
  const handleLogoClick = (e) => {
    e?.preventDefault();
    console.log('Logo clicked - navigating to authentication portal');
    navigate('/authentication-login-portal');
  };

  // Updated navigation items - added Schedule Appointment tab with Calendar icon
  const navigationItems = [
    { name: 'Home', href: '/', icon: 'Home' },
    { name: 'About', href: '/about-us', icon: 'Users' },
    { name: 'Services', href: '/services', icon: 'Briefcase' },
    { name: 'Schedule', href: '/appointment-scheduling-system', icon: 'Calendar', fullName: 'Schedule Appointment' },
    { name: 'Locations', href: '/locations', icon: 'MapPin' },
    { name: 'Blog', href: '/blog', icon: 'BookOpen' },
    { name: 'Gear', href: '/gear', icon: 'ShoppingBag' },
    { name: 'Inmate Search', href: '/inmate-search-widget', icon: 'Search' },
    { name: 'Contact', href: '/contact-us', icon: 'Phone' }
  ];

  return (
    <>
      {/* Top Phone Banner - Highly Visible with Client Access Button */}
      <div className="bg-gradient-to-r from-red-600 via-red-500 to-red-600 text-white py-2 px-4 shadow-lg">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-center text-center space-y-1 sm:space-y-0 sm:space-x-6">
            {/* Client Access Button - Blue with white text, positioned on the left */}
            <a 
              href="https://agents.simplybail.com/m/sailorsbailbonds43/office?code"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-blue-600 text-white px-4 py-1.5 rounded-full font-bold hover:bg-blue-700 transition-colors text-sm flex items-center"
              aria-label="Access your bail account information"
            >
              <Shield className="w-4 h-4 mr-2" aria-hidden="true" />
              Client Access
            </a>
            
            {/* NEW: Staff/Admin Login Button */}
            <Link
              to="/authentication-login-portal"
              className="bg-gray-800 text-white px-4 py-1.5 rounded-full font-bold hover:bg-gray-900 transition-colors text-sm flex items-center border border-gray-600"
              aria-label="Staff and administrator login portal"
            >
              <Lock className="w-4 h-4 mr-2" aria-hidden="true" />
              Staff Login
            </Link>
            
            <div className="flex items-center text-sm font-medium">
              <div className="w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse" aria-hidden="true"></div>
              <span>24/7 Emergency Bail Service Available</span>
            </div>
            <a 
              href={`tel:${fallbackPhone}`}
              className="bg-white text-red-600 px-4 py-1.5 rounded-full font-bold hover:bg-red-50 transition-colors text-sm flex items-center"
              aria-label={`Call Sailors Bail Bonds at ${formatPhoneDisplay(fallbackPhone)}`}
            >
              <Phone className="w-4 h-4 mr-2" aria-hidden="true" />
              Call Now: {formatPhoneDisplay(fallbackPhone)}
            </a>
          </div>
        </div>
      </div>

      <header className={`bg-card border-b border-border shadow-soft ${className}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo and Brand - Enhanced larger, more prominent position - NOW CLICKABLE WITH DIRECT HANDLER */}
            <div className="flex items-center space-x-4 flex-shrink-0 relative z-50">
              {/* Clickable Logo linking to Authentication Portal with direct onClick handler */}
              <Link 
                to="/authentication-login-portal"
                onClick={handleLogoClick}
                className="flex-shrink-0 group relative z-[9999] block"
                aria-label="Click logo to access authentication portal"
                style={{ 
                  pointerEvents: 'auto !important',
                  cursor: 'pointer !important',
                  position: 'relative',
                  zIndex: 9999
                }}
              >
                <img 
                  src="/assets/images/162111a8-97b7-4fd6-a950-171d24f8aaac-1764544825148.png" 
                  alt="Sailors Bail Bonds - Professional nautical-themed logo featuring anchor with rope in teal and gold - Click to access authentication portal"
                  className="h-14 w-auto object-contain md:h-16 lg:h-18 drop-shadow-sm hover:drop-shadow-md transition-all duration-300 cursor-pointer group-hover:scale-105"
                  style={{ cursor: 'pointer !important', pointerEvents: 'auto !important' }}
                />
              </Link>
              
              {/* Mobile Brand - Simplified for smaller screens */}
              <div className="sm:hidden">
                <h1 className="text-lg font-bold text-foreground">
                  Sailors Bail Bonds
                </h1>
                <p className="text-xs text-muted-foreground -mt-0.5">
                  CA LIC# 1N13041
                </p>
              </div>
            </div>

            {/* Desktop Navigation - GUARANTEED VISIBILITY WITH ALL TABS */}
            <nav className="hidden sm:flex items-center gap-1 lg:gap-2 mx-2" aria-label="Primary navigation">
              {navigationItems?.map((item) => (
                <a
                  key={item?.name}
                  href={item?.href}
                  className="flex items-center gap-1 px-1.5 lg:px-2 py-2 rounded-md text-xs lg:text-sm font-medium text-foreground hover:text-primary hover:bg-muted transition-colors whitespace-nowrap"
                  title={item?.fullName || item?.name}
                >
                  <Icon name={item?.icon} size={18} className="flex-shrink-0" />
                  <span className="hidden lg:inline">{item?.name}</span>
                </a>
              ))}
            </nav>

            {/* 24/7 Emergency Bail Button - Only on 2XL screens to ensure navigation space */}
            <div className="hidden 2xl:flex items-center flex-shrink-0 ml-2">
              <a 
                href={`tel:${fallbackPhone}`}
                className="bg-gradient-to-r from-red-600 to-red-700 text-white px-3 py-2 rounded-xl font-bold text-sm hover:from-red-700 hover:to-red-800 transition-all flex items-center whitespace-nowrap shadow-lg"
                aria-label="24/7 Emergency Bail assistance"
              >
                <Phone className="w-4 h-4 mr-2" aria-hidden="true" />
                <span>24/7 Emergency</span>
              </a>
            </div>

            {/* User Menu - Ultra-compact to maximize navigation space */}
            <div className="flex items-center justify-end flex-shrink-0 ml-1">
              {loading ? (
                <div className="flex items-center space-x-1">
                  <div className="w-4 h-4 border-2 border-muted-foreground border-t-primary rounded-full animate-spin"></div>
                </div>
              ) : user ? (
                <>
                  <div className="hidden xl:block text-right mr-2">
                    <p className="text-xs font-medium text-foreground truncate max-w-[100px]">
                      {userProfile?.full_name || user?.email}
                    </p>
                  </div>
                  
                  <button
                    onClick={handleSignOut}
                    className="flex items-center p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
                    title="Sign Out"
                  >
                    <Icon name="LogOut" size={16} />
                    <span className="text-xs hidden xl:inline ml-1">Out</span>
                  </button>
                </>
              ) : null}
            </div>

            {/* Mobile menu button - NOW ONLY SHOWS ON MOBILE (below 640px) */}
            <div className="sm:hidden ml-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleMobileMenu}
                className="inline-flex items-center justify-center p-2 rounded-md text-foreground hover:text-primary hover:bg-muted"
              >
                <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} size={24} />
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation Menu - ONLY SHOWS ON MOBILE (below 640px) */}
        {isMobileMenuOpen && (
          <div className="sm:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-card border-t border-border">
              {/* Mobile Logo Display - Enhanced prominence with clickability */}
              <div className="flex flex-col items-center py-6 border-b border-border mb-4 bg-gradient-to-br from-muted/30 to-muted/50 rounded-lg">
                <Link
                  to="/authentication-login-portal"
                  onClick={handleLogoClick}
                  className="block relative z-[9999]"
                  aria-label="Click logo to access authentication portal"
                  style={{ 
                    pointerEvents: 'auto !important',
                    cursor: 'pointer !important',
                    zIndex: 9999
                  }}
                >
                  <img 
                    src="/assets/images/162111a8-97b7-4fd6-a950-171d24f8aaac-1764544825148.png" 
                    alt="Sailors Bail Bonds - Professional nautical-themed logo featuring anchor with rope in teal and gold"
                    className="h-16 w-auto object-contain mb-3 drop-shadow-md hover:scale-105 transition-transform duration-300 cursor-pointer"
                    style={{ cursor: 'pointer !important', pointerEvents: 'auto !important' }}
                  />
                </Link>
                <h2 className="text-xl font-bold text-foreground text-center">
                  Sailors Bail Bonds
                </h2>
                <p className="text-sm text-muted-foreground text-center mt-1">
                  Professional Bail Services
                </p>
                <span className="text-xs text-muted-foreground font-medium bg-muted px-3 py-1 rounded-full mt-2">
                  CA LIC# 1N13041
                </span>
                
                {/* Mobile Phone Display */}
                <a 
                  href={`tel:${fallbackPhone}`}
                  className="mt-4 bg-gradient-to-r from-red-600 to-red-700 text-white px-6 py-3 rounded-xl font-bold text-lg hover:from-red-700 hover:to-red-800 transition-all flex items-center"
                  aria-label={`Call Sailors Bail Bonds ${formatPhoneDisplay(fallbackPhone)}`}
                >
                  <Phone className="w-5 h-5 mr-2" aria-hidden="true" />
                  {formatPhoneDisplay(fallbackPhone)}
                </a>
              </div>

              {navigationItems?.map((item) => (
                <a
                  key={item?.name}
                  href={item?.href}
                  className="flex items-center space-x-3 px-3 py-2 rounded-md text-base font-medium text-foreground hover:text-primary hover:bg-muted micro-interaction"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <Icon name={item?.icon} size={20} />
                  <span>{item?.fullName || item?.name}</span>
                </a>
              ))}
            </div>
          </div>
        )}
      </header>
    </>
  );
};

export default Header;